import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, X } from 'lucide-react';
import { api, ApiError } from './api';
import { useAuth, AdminRole } from './AuthContext';

interface AdminRow {
  id: number;
  name: string;
  email: string;
  role: AdminRole;
  created_at: string;
}

interface FormState {
  id: number | null;
  name: string;
  email: string;
  password: string;
  role: AdminRole;
}

const emptyForm: FormState = { id: null, name: '', email: '', password: '', role: 'admin' };

const AdminAdmins: React.FC = () => {
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api.get<{ admins: AdminRow[] }>('/api/admins')
      .then(({ admins }) => setAdmins(admins))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load admins.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => { setForm(emptyForm); setFormError(null); setFormOpen(true); };
  const openEdit = (a: AdminRow) => {
    setForm({ id: a.id, name: a.name, email: a.email, password: '', role: a.role });
    setFormError(null);
    setFormOpen(true);
  };
  const closeForm = () => setFormOpen(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      if (form.id === null) {
        if (!form.password || form.password.length < 8) {
          throw new Error('Password must be at least 8 characters.');
        }
        await api.post('/api/admins', { name: form.name, email: form.email, password: form.password, role: form.role });
      } else {
        const payload: Partial<FormState> = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/api/admins/${form.id}`, payload);
      }
      setFormOpen(false);
      load();
    } catch (err) {
      setFormError(err instanceof ApiError || err instanceof Error ? err.message : 'Failed to save admin.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: AdminRow) => {
    if (!window.confirm(`Remove ${a.name} (${a.email}) as an admin?`)) return;
    setDeletingId(a.id);
    try {
      await api.delete(`/api/admins/${a.id}`);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete admin.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-figtree font-black text-2xl text-white">Admins</h1>
          <p className="text-neutral-500 text-sm font-exo">Manage who can access this dashboard</p>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 bg-accent-amber text-bg-dark px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-white transition-all shrink-0">
          <Plus size={14} /> Add Admin
        </button>
      </div>

      <div className="bg-neutral-900 border border-white/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-4 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Name</th>
                <th className="px-4 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Email</th>
                <th className="px-4 sm:px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Role</th>
                <th className="hidden sm:table-cell px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-neutral-500">Added</th>
                <th className="px-4 sm:px-5 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500">Loading…</td></tr>
              ) : error ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-red-400">{error}</td></tr>
              ) : (
                admins.map(a => (
                  <tr key={a.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 sm:px-5 py-3.5 text-white">{a.name}</td>
                    <td className="px-4 sm:px-5 py-3.5 text-neutral-300 break-all">{a.email}</td>
                    <td className="px-4 sm:px-5 py-3.5">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${
                        a.role === 'super_admin'
                          ? 'text-accent-amber bg-accent-amber/10 border-accent-amber/20'
                          : 'text-neutral-400 bg-white/5 border-white/10'
                      }`}>
                        {a.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-5 py-3.5 text-neutral-500 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 sm:px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => openEdit(a)} className="text-neutral-500 hover:text-white transition-colors" aria-label="Edit admin">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => handleDelete(a)} disabled={deletingId === a.id || a.id === currentAdmin?.id}
                          className="text-neutral-500 hover:text-red-400 disabled:opacity-30 disabled:hover:text-neutral-500 transition-colors" aria-label="Delete admin">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 sm:px-6 z-50" onClick={closeForm}>
          <div className="bg-neutral-900 border border-white/10 rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-figtree font-bold text-lg text-white">{form.id === null ? 'Add Admin' : 'Edit Admin'}</h2>
              <button onClick={closeForm} className="text-neutral-500 hover:text-white"><X size={18} /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Name</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-neutral-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-accent-amber transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Email</label>
                <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-neutral-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-accent-amber transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">
                  {form.id === null ? 'Password' : 'New Password (optional)'}
                </label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={form.id === null ? 'At least 8 characters' : 'Leave blank to keep current password'}
                  className="w-full bg-neutral-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-accent-amber transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Role</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as AdminRole }))}
                  className="w-full bg-neutral-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-accent-amber transition-all">
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              {formError && (
                <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">{formError}</p>
              )}

              <button type="submit" disabled={saving}
                className="w-full py-3 rounded-xl bg-accent-amber text-bg-dark font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white disabled:opacity-60 transition-all">
                {saving ? 'Saving…' : form.id === null ? 'Add Admin' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAdmins;
